<script setup lang="ts">
const props = withDefaults( defineProps<{
  tags?: string[],
  size?: string,
  max?: number | string,
}>(), {
  tags: () => [],
  size: 'md',
} );

const tagResource = useTagResource();
const topicsResponse = await tagResource.getTopics();

const topics = computed( () => topicsResponse.data.value || [] );
  
const intlMax = computed<number | null>(() => {
  if (props.max != null) {
    return typeof props.max === 'number' ? props.max : Number.parseInt(props.max, 10);
  } else {
    return null;
  }
});

const renderTags = computed<string[]>(() => {
  const result: string[] = [];

  if (props.tags && props.tags.length) {
    const max = Math.min(intlMax.value || props.tags.length, props.tags.length);

    for (let i = 0; i < max; i += 1) {
      const tag = topics.value.find( x => x.topicTagId === props.tags[i] );
      if ( tag ) {
        result.push( tag?.name.replace( '-', '\u2011' ) );

        if ( intlMax.value && tag.name.length > 15 ) {
          i += 1;
        }
      }
    }
  }

  return result;
});

const moreCount = computed<number>(() => {
  let result = 0;
  if (intlMax.value && props.tags && renderTags.value) {
    const tLen = props.tags.length;
    const rtLen = renderTags.value.length;

    result = tLen > rtLen ? tLen - rtLen : result;
  }

  return result;
});

function getComma(index: number): string {
  return (moreCount.value && index > renderTags.value.length - 1) ||
  (!moreCount.value && index > renderTags.value.length - 2)
    ? ''
    : ',';
}
</script>

<template>
  <div class="tag-list">
    <ul v-if="renderTags.length" class="tag-list__tags">
      <li
        v-for="(tag, index) in renderTags"
        :key="index"
        class="tag-list__tag"
      >
        <span :class="`tag-list__text tag-list__text--${size}`">
          {{ tag }}{{ getComma(index) }}
        </span>
        <wbr />
      </li>
      <li v-if="moreCount" class="tag-list__more">
        <span :class="`tag-list__text tag-list__text--${size}`">
          +{{ moreCount }} more
        </span>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.tag-list {
  &__tags {
    display: flex;
    flex-wrap: wrap;
    padding: 0;
    margin: 0;
    list-style: none;
    line-height: 1;
    gap: 0.3em;
  }

  &__tag {
    display: inline;
    white-space: nowrap;

    &::before {
      content: '#';
      color: var(--primary);
    }

    &:not(:last-child)::after {
      margin-right: 0.3em;
    }
  }

  &__more {
    display: inline;
  }

  &__text {
    white-space: normal;

    &--xs {
      font-size: 0.5rem;
    }

    &--sm {
      font-size: 0.8rem;
    }

    &--lg {
      font-size: 1.2rem;
    }

    &--xl {
      font-size: 1.5rem;
    }
  }
}
</style>
